/** Typed fetch wrapper for the JSON API. Carries the CSRF token on mutations. */
import type {
  AccountKeyInfo,
  ApiTokenInfo,
  AuditEntry,
  ChangePasswordRequest,
  EmlPreview,
  FlagUpdate,
  FullMessage,
  LoginRequest,
  Mailbox,
  MailboxCreateRequest,
  MailboxRenameRequest,
  MessagesPage,
  MoveRequest,
  RetentionPolicy,
  SaveDraftRequest,
  SaveDraftResponse,
  SearchResult,
  SendRequest,
  SendResponse,
  SessionInfo,
  SoftDeleteEntry,
  UpdatePreferencesRequest,
} from "../shared/types";

let csrfToken = "";
export function setCsrf(token: string): void {
  csrfToken = token;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const mutating = init.method !== undefined && init.method !== "GET";
  if (init.body) headers.set("Content-Type", "application/json");
  if (mutating && csrfToken) headers.set("X-CSRF-Token", csrfToken);

  const res = await fetch(path, { credentials: "same-origin", ...init, headers });
  if (res.status === 204) return undefined as T;

  const data = await safeJson(res);
  if (!res.ok) {
    const body = data as { error?: string; code?: string } | null;
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`, body?.code);
  }
  return data as T;
}

/**
 * Human-friendly messages for the password-change endpoint, keyed by the
 * server's error `code`. Falls back to the server message (or a generic line)
 * when a code has no dedicated copy.
 */
const PASSWORD_CHANGE_MESSAGES: Record<string, string> = {
  current_password_invalid: "Your current password is incorrect.",
  // `password_policy` is intentionally omitted: the server returns a
  // dynamically-correct message (e.g. "Password must be 12–64 characters")
  // built from the live policy bounds. We pass that through instead of
  // hardcoding stale copy that would drift if the bounds change.
  password_same: "The new password must be different from your current password.",
  rate_limited: "Too many attempts, please wait and try again.",
  change_in_progress: "A password change is already in progress. Please wait a moment and try again.",
  feature_disabled: "Password change is currently unavailable.",
  password_change_failed: "The server could not change your password. Please try again later.",
  bridge_timeout: "The password change timed out. Please try again.",
  csrf: "Your session has expired. Please refresh the page and try again.",
};

function friendlyPasswordChangeError(err: unknown): ApiError {
  if (err instanceof ApiError) {
    const friendly = err.code ? PASSWORD_CHANGE_MESSAGES[err.code] : undefined;
    return new ApiError(err.status, friendly ?? err.message, err.code);
  }
  return new ApiError(0, "Something went wrong. Please try again.");
}

export const api = {
  session: () => request<SessionInfo>("/api/auth/session"),
  login: (body: LoginRequest) =>
    request<SessionInfo>("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  mailboxes: {
    list: () => request<Mailbox[]>("/api/mailboxes"),
    create: (body: MailboxCreateRequest) =>
      request<Mailbox>("/api/mailboxes", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    rename: (body: MailboxRenameRequest) =>
      request<Mailbox>("/api/mailboxes", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    delete: (path: string) =>
      request<void>(`/api/mailboxes?path=${encodeURIComponent(path)}`, {
        method: "DELETE",
      }),
  },
  messages: (mailbox: string, page: number) =>
    request<MessagesPage>(
      `/api/messages?mailbox=${encodeURIComponent(mailbox)}&page=${page}`,
    ),
  message: (mailbox: string, uid: number, remote = false) =>
    request<FullMessage>(
      `/api/messages/${uid}?mailbox=${encodeURIComponent(mailbox)}${remote ? "&remote=1" : ""}`,
    ),
  send: (body: SendRequest) =>
    request<SendResponse>("/api/messages/send", { method: "POST", body: JSON.stringify(body) }),
  updateFlags: (mailbox: string, uid: number, body: FlagUpdate) =>
    request<{ seen: boolean; flagged: boolean }>(
      `/api/messages/${uid}/flags?mailbox=${encodeURIComponent(mailbox)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  moveMessage: (uid: number, mailbox: string, destination: string) =>
    request<void>(`/api/messages/${uid}/move`, {
      method: "POST",
      body: JSON.stringify({ mailbox, destination } satisfies MoveRequest),
    }),
  deleteMessage: (mailbox: string, uid: number) =>
    request<void>(`/api/messages/${uid}?mailbox=${encodeURIComponent(mailbox)}`, {
      method: "DELETE",
    }),
  attachmentUrl: (mailbox: string, uid: number, partId: string) =>
    `/api/messages/${uid}/attachments/${partId}?mailbox=${encodeURIComponent(mailbox)}`,
  attachmentPreview: (mailbox: string, uid: number, partId: string) =>
    request<EmlPreview>(
      `/api/messages/${uid}/attachments/${partId}/preview?mailbox=${encodeURIComponent(mailbox)}`,
    ),
  search: (mailbox: string, q: string) =>
    request<SearchResult>(
      `/api/search?mailbox=${encodeURIComponent(mailbox)}&q=${encodeURIComponent(q)}`,
    ),
  tokens: {
    list: () => request<ApiTokenInfo[]>("/api/tokens"),
    create: (body: { label: string; scope: string; expiresInDays?: number }) =>
      request<{ token: string; label: string; scope: string }>("/api/tokens", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    revoke: (id: number) =>
      request<void>(`/api/tokens/${id}`, { method: "DELETE" }),
  },
  drafts: {
    save: (body: SaveDraftRequest) =>
      request<SaveDraftResponse>("/api/messages/drafts", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (uid: number, body: SaveDraftRequest) =>
      request<SaveDraftResponse>(`/api/messages/drafts/${uid}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
  accountKeys: {
    list: () => request<AccountKeyInfo[]>("/api/account-keys"),
    create: (body: { label: string; expiresInDays?: number }) =>
      request<{ key: string; label: string }>("/api/account-keys", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    revoke: (id: number) =>
      request<void>(`/api/account-keys/${id}`, { method: "DELETE" }),
  },
  account: {
    /**
     * Change the signed-in user's own mailbox password. Resolves on `204`;
     * rejects with an {@link ApiError} whose message is already friendly.
     * The target mailbox is derived server-side from the session — the body
     * carries no email/localpart/target.
     */
    changePassword: (body: ChangePasswordRequest): Promise<void> =>
      request<void>("/api/account/password", {
        method: "POST",
        body: JSON.stringify(body),
      }).catch((e: unknown) => {
        throw friendlyPasswordChangeError(e);
      }),
    /**
     * Update the signed-in user's own remote-content default. Resolves with the
     * server-confirmed value. The target account is derived server-side from the
     * session — the body carries no email/target.
     */
    updatePreferences: (body: UpdatePreferencesRequest): Promise<UpdatePreferencesRequest> =>
      request<UpdatePreferencesRequest>("/api/account/preferences", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },
  admin: {
    users: () =>
      request<Array<{ email: string; localpart: string }>>("/api/admin/users"),
    tokens: () =>
      request<Array<{
        id: number;
        label: string;
        email: string;
        scope: string;
        createdAt: number;
        lastUsedAt: number | null;
        expiresAt: number | null;
        revoked: boolean;
      }>>("/api/admin/tokens"),
    audit: (limit?: number) =>
      request<{ entries: AuditEntry[]; total: number }>(
        `/api/admin/audit?limit=${limit ?? 100}`,
      ),
    softDeletes: (limit?: number) =>
      request<SoftDeleteEntry[]>(
        `/api/admin/soft-deletes?limit=${limit ?? 100}`,
      ),
    retention: {
      list: () => request<RetentionPolicy[]>("/api/admin/retention"),
      upsert: (body: { mailboxPath: string; maxAgeDays: number }) =>
        request<RetentionPolicy[]>("/api/admin/retention", {
          method: "POST",
          body: JSON.stringify(body),
        }),
      delete: (mailboxPath: string) =>
        request<void>(`/api/admin/retention/${encodeURIComponent(mailboxPath)}`, {
          method: "DELETE",
        }),
    },
  },
};
